import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { localizeTour } from '../lib/tourI18n';
import { 
  Clock, 
  Users, 
  ChevronRight,
  Filter,
  MapPin,
  AlertCircle,
  Check,
  UtensilsCrossed
} from 'lucide-react';

const ToursPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: t('tours.all'), icon: '🌴' },
    { id: 'transfer', name: t('tours.transfers'), icon: '🚐' },
    { id: 'atv', name: t('tours.atvBuggy'), icon: '🏎️' },
    { id: 'zipline', name: t('tours.ziplineTitle'), icon: '🦅' },
    { id: 'horseback', name: t('tours.horsebackTitle'), icon: '🐎' },
    { id: 'hiking', name: t('tours.hiking'), icon: '🥾' },
    { id: 'rafting', name: t('tours.rafting'), icon: '🚣' },
    { id: 'combo', name: t('tours.combos'), icon: '⭐' }
  ];

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const category = activeCategory === 'all' ? null : activeCategory;
        const data = await api.getTours(category);
        setTours(data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [activeCategory]);

  const getCategoryOrder = (category, tourId) => {
    // Manuel Antonio and Kayak Damas go right after transfers (position 1.5 and 1.6)
    if (tourId === 'tour-combo-manuel-antonio') return 1.5;
    if (tourId === 'tour-combo-kayak-damas') return 1.6;
    const order = { transfer: 1, atv: 2, zipline: 3, horseback: 4, rafting: 4.5, combo: 5 };
    return order[category] || 99;
  };

  // Sort tours: by category first, Manuel Antonio and Kayak after transfers, then Full Day combos before Half Day
  const sortedTours = [...tours].sort((a, b) => {
    const catDiff = getCategoryOrder(a.category, a.id) - getCategoryOrder(b.category, b.id);
    if (catDiff !== 0) return catDiff;
    
    // Within combos, sort Full Day first, then by price descending
    if (a.category === 'combo' && b.category === 'combo') {
      const aFullDay = a.duration?.toLowerCase().includes('full');
      const bFullDay = b.duration?.toLowerCase().includes('full');
      if (aFullDay && !bFullDay) return -1;
      if (!aFullDay && bFullDay) return 1;
      return b.price - a.price; // Higher price first within same duration type
    }
    return 0;
  });

  const formatPrice = (tour) => {
    // Special case for Manuel Antonio and Kayak Damas - transport base price
    if (tour.id === 'tour-combo-manuel-antonio') {
      return { price: tour.price, label: t('tours.transportPlusTours'), isSpecial: true };
    }
    if (tour.id === 'tour-combo-kayak-damas') {
      return { price: tour.price, label: t('tours.transportPlusTour'), isSpecial: true };
    }
    // Bijagual: show $20/person only. Transport ($160 up to 5 + $10/extra) is disclosed on booking page.
    if (tour.id === 'tour-bijagual-waterfall') {
      return { price: 20, label: t('tours.perPersonLabel') };
    }
    // ATV / Buggy / GIMME ALL Buggy combos: title already says "(N Pax)" — omit label to avoid confusion
    const ATV_IDS = ['tour-atv-2hrs', 'tour-atv-3hrs', 'tour-atv-4hrs'];
    const BUGGY_IDS = ['tour-buggy-2hrs', 'tour-buggy-3hrs', 'tour-buggy-4hrs'];
    const GIMME_BUGGY_FLAT_IDS = [
      'tour-combo-gimme-all-double',
      'tour-combo-gimme-all-atv-double',
      'tour-combo-gimme-all-buggy-3pax',
      'tour-combo-gimme-all-buggy-4pax',
    ];
    if (ATV_IDS.includes(tour.id) || BUGGY_IDS.includes(tour.id) || GIMME_BUGGY_FLAT_IDS.includes(tour.id)) {
      return { price: tour.price, label: '' };
    }
    if (tour.max_passengers === 1) {
      return { price: tour.price, label: t('tours.perPersonLabel') };
    } else if (tour.max_passengers === 2) {
      return { price: tour.price, label: t('tours.per2People') };
    } else if (tour.category === 'transfer') {
      return { price: tour.price, label: t('tours.perUpTo5Pax') };
    }
    return { price: tour.price, label: t('tours.perPersonLabel') };
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#1D4E41]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            {t('tours.pageTitle')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('tours.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  activeCategory === cat.id
                    ? 'bg-[#1D4E41] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Info Banner - Only show for specific tour categories (not all services or transfers) */}
      {activeCategory !== 'transfer' && activeCategory !== 'all' && activeCategory !== 'rafting' && (
        <section className="py-4 bg-[#E8F5E9]">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#1D4E41]">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t('tours.transportIncluded')}
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t('tours.fruitsHydration')}
              </span>
              <span className="flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4" />
                {t('tours.lunchOption')}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Tours Grid */}
      <section className="py-16" data-testid="tours-grid">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-56 bg-gray-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-16">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">{t('tours.noToursFound')}</h3>
              <p className="text-gray-500 mt-2">{t('tours.tryDifferent')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedTours.map((tour, idx) => {
                const tl = localizeTour(tour, language);
                const priceInfo = formatPrice(tour);
                const isCombo = tour.category === 'combo';
                const isRafting = tour.category === 'rafting';
                const hasLunchIncluded = tl.highlights?.some(h => h.includes('LUNCH'));
                
                return (
                  <div
                    key={tour.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/tours/${tour.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/tours/${tour.id}`); } }}
                    className={`group relative overflow-hidden rounded-2xl bg-white border transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:-translate-y-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1D4E41] focus:ring-offset-2 ${
                      isCombo ? 'border-[#F08A5D]/30 ring-2 ring-[#F08A5D]/20' : 'border-gray-100 hover:border-[#1D4E41]/20'
                    }`}
                    data-testid={`tour-item-${idx}`}
                  >
                    {/* Best Value Badge for Combos */}
                    {isCombo && hasLunchIncluded && (
                      <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-[#F08A5D] text-white rounded-full text-xs font-semibold">
                        {t('tours.lunchIncluded')}
                      </div>
                    )}
                    
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={tour.image_url}
                        alt={tl.title}
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      {tour.discount_percent ? (
                        <div className="absolute top-4 left-4 z-10 flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded-full text-xs font-bold shadow-md" data-testid={`green-season-badge-${idx}`}>
                          🌿 {language === 'es' ? (tour.discount_label_es || 'Temporada Verde') : (tour.discount_label || 'Green Season')} -{tour.discount_percent}%
                        </div>
                      ) : null}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#1D4E41] transition-colors">
                        {tl.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {tl.description}
                      </p>
                      
                      {/* Highlights */}
                      {tl.highlights && tl.highlights.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {tl.highlights.slice(0, 3).map((highlight, i) => (
                            <span 
                              key={i} 
                              className={`text-xs px-2 py-1 rounded ${
                                highlight.includes('LUNCH') 
                                  ? 'bg-[#F08A5D]/10 text-[#F08A5D] font-medium' 
                                  : 'bg-[#E8F5E9] text-[#1D4E41]'
                              }`}
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {tl.duration}
                        </span>
                        {!isRafting && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {tour.max_passengers === 1 ? t('tours.onePerson') : 
                             tour.max_passengers === 2 ? t('tours.twoPeople') : 
                             `${t('tours.upToLabel')} ${tour.max_passengers}`}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div>
                          {tour.discount_percent ? (
                            <>
                              <span className="text-base text-gray-400 line-through mr-2">${priceInfo.price}</span>
                              <span className="text-2xl font-bold text-[#1D4E41]">${Math.round(priceInfo.price * (1 - tour.discount_percent / 100) * 100) / 100}</span>
                            </>
                          ) : (
                            <span className="text-2xl font-bold text-[#1D4E41]">${priceInfo.price}</span>
                          )}
                          <span className="text-gray-500 text-sm"> {priceInfo.label}</span>
                        </div>
                        <Link
                          to={`/booking/${tour.id}`}
                          data-testid={`book-btn-${idx}`}
                          onClick={(e) => e.stopPropagation()}
                          className="h-11 px-6 bg-[#F08A5D] hover:bg-[#E0794C] text-white rounded-full font-medium flex items-center gap-1 transition-all active:scale-95"
                        >
                          {t('tours.bookNow')}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Requirements & Policies - hidden for rafting (different region: La Fortuna/Arenal) */}
      {activeCategory !== 'rafting' && (
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-8 text-center">
            {t('tours.requirementsTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* ATV Requirements */}
            <div className="p-6 bg-[#FAFAF8] rounded-xl">
              <h3 className="font-semibold text-[#1D4E41] mb-3 flex items-center gap-2">
                <span>🏎️</span> {t('tours.atvTitle')}
              </h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.atvReq1')}
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.atvReq2')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.atvReq3')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.atvReq4')}
                </li>
              </ul>
            </div>

            {/* Zipline Requirements */}
            <div className="p-6 bg-[#FAFAF8] rounded-xl">
              <h3 className="font-semibold text-[#1D4E41] mb-3 flex items-center gap-2">
                <span>🦅</span> {t('tours.ziplineTitle')}
              </h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.ziplineReq1')}
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.ziplineReq2')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.ziplineReq3')}
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.ziplineReq4')}
                </li>
              </ul>
            </div>

            {/* Horseback Requirements */}
            <div className="p-6 bg-[#FAFAF8] rounded-xl">
              <h3 className="font-semibold text-[#1D4E41] mb-3 flex items-center gap-2">
                <span>🐎</span> {t('tours.horsebackTitle')}
              </h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#F08A5D] flex-shrink-0 mt-0.5" />
                  {t('tours.horsebackReq1')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.horsebackReq2')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.horsebackReq3')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                  {t('tours.horsebackReq4')}
                </li>
              </ul>
            </div>

            {/* General Info - Only show for specific tour categories */}
            {activeCategory !== 'transfer' && activeCategory !== 'all' && (
              <div className="p-6 bg-[#FAFAF8] rounded-xl">
                <h3 className="font-semibold text-[#1D4E41] mb-3 flex items-center gap-2">
                  <span>ℹ️</span> {t('tours.generalTitle')}
                </h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                    {t('tours.genReq1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                    {t('tours.genReq2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                    {t('tours.genReq3')}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                    {t('tours.genReq4')}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#1D4E41]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            {t('tours.customCtaTitle')}
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            {t('tours.customCtaSubtitle')}
          </p>
          <a
            href="https://wa.me/50688173167?text=Hello!%20I%20would%20like%20to%20inquire%20about%20a%20custom%20tour."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="custom-tour-cta"
            className="inline-flex items-center gap-2 h-14 px-8 bg-[#F08A5D] hover:bg-[#E0794C] text-white rounded-full font-semibold transition-all active:scale-95"
          >
            {t('tours.customCtaButton')}
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ToursPage;
